import { useState } from "react";
import { X } from "lucide-react";
import { publishingApi } from "@/lib/api";

const INQUIRY_TYPES = [
  { value: "publishing", label: "Publishing" },
  { value: "licensing", label: "Licensing" },
  { value: "film-tv", label: "Film & Television" },
  { value: "adaptation", label: "Adaptation" },
  { value: "partnership", label: "Brand Partnership" },
  { value: "other", label: "Other" },
];

export const BusinessInquiryModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    name: "", email: "", company: "", inquiry_type: "publishing", message: ""
  });
  const [state, setState] = useState({ loading: false, success: false, error: "" });

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false, error: "" });
    try {
      await publishingApi.submitInquiry(form);
      setState({ loading: false, success: true, error: "" });
      setTimeout(() => { onClose(); setState({ loading: false, success: false, error: "" }); }, 2500);
    } catch (err) {
      setState({ loading: false, success: false, error: "Something went wrong. Please try again." });
    }
  };

  return (
    <div 
      data-testid="inquiry-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative bg-primary border border-jcrown-gold/30 max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          data-testid="close-inquiry"
          className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 md:p-12">
          {state.success ? (
            <div className="text-center py-12">
              <h3 className="font-heading text-white text-3xl tracking-tight uppercase mb-4">Received</h3>
              <p className="text-white/60 font-body">Thanks for reaching out. We'll be in touch.</p>
            </div>
          ) : (
            <>
              <p className="text-jcrown-gold/70 text-[10px] tracking-[0.3em] uppercase mb-3 font-body">
                Goodman Publishing
              </p>
              <h3 className="font-heading text-white text-3xl md:text-4xl tracking-tight uppercase mb-2">
                Business Inquiries
              </h3>
              <p className="text-white/50 font-body text-sm mb-8">
                Publishing • Licensing • Film & TV • Partnerships
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input 
                    type="text" required placeholder="Name"
                    data-testid="inquiry-name"
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="w-full bg-black/40 border border-white/10 focus:border-jcrown-gold/50 text-white px-4 py-3 font-body text-sm outline-none transition-colors placeholder:text-white/30"
                  />
                  <input 
                    type="email" required placeholder="Email"
                    data-testid="inquiry-email"
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    className="w-full bg-black/40 border border-white/10 focus:border-jcrown-gold/50 text-white px-4 py-3 font-body text-sm outline-none transition-colors placeholder:text-white/30"
                  />
                </div>
                <input 
                  type="text" placeholder="Company (optional)"
                  data-testid="inquiry-company"
                  value={form.company}
                  onChange={(e) => setForm({...form, company: e.target.value})}
                  className="w-full bg-black/40 border border-white/10 focus:border-jcrown-gold/50 text-white px-4 py-3 font-body text-sm outline-none transition-colors placeholder:text-white/30"
                />
                <select 
                  required
                  data-testid="inquiry-type"
                  value={form.inquiry_type}
                  onChange={(e) => setForm({...form, inquiry_type: e.target.value})}
                  className="w-full bg-black/40 border border-white/10 focus:border-jcrown-gold/50 text-white px-4 py-3 font-body text-sm outline-none transition-colors"
                >
                  {INQUIRY_TYPES.map(t => (
                    <option key={t.value} value={t.value} className="bg-primary">{t.label}</option>
                  ))}
                </select>
                <textarea 
                  required placeholder="Tell us about your inquiry..." rows="4"
                  data-testid="inquiry-message"
                  value={form.message}
                  onChange={(e) => setForm({...form, message: e.target.value})}
                  className="w-full bg-black/40 border border-white/10 focus:border-jcrown-gold/50 text-white px-4 py-3 font-body text-sm outline-none transition-colors placeholder:text-white/30 resize-none"
                />
                {state.error && (
                  <p className="text-red-400 font-body text-sm">{state.error}</p>
                )}
                <button 
                  type="submit"
                  disabled={state.loading}
                  data-testid="inquiry-submit"
                  className="w-full bg-jcrown-gold text-jcrown-dark px-8 py-4 font-body text-sm tracking-widest uppercase hover:bg-white hover:text-black transition-all active:scale-95 disabled:opacity-50"
                >
                  {state.loading ? "Sending..." : "Send Inquiry"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
